/**
 * Achievement badges — ten action types, seven ranks each, and what a rank buys.
 *
 * ⭐⭐ ONE LADDER SHAPE FOR EVERY ACTION. The page used to hard-code four action
 * types with their thresholds inline and their cards duplicated four times in
 * JSX. Adding six more that way would have been six more copies of the same
 * block, each free to drift. Everything here is data: a type is its counting
 * rule plus two arrays of seven.
 *
 * ⚠️⚠️ CAPACITY IS A **PER-DOMAIN ALLOWANCE, AND IT IS ENFORCED** (owner,
 * 2026-08-03 — a deliberate REVERSAL of the "never blocks" rule this header
 * carried earlier the same day). `baseCapacity` + `capacity[7]` say how many of
 * a thing a DOMAIN may hold at each badge rank. `capacityFor` is read by the
 * capacity indicator on each action type's page AND by `utils/capacityGuard.ts`,
 * which stops a create when `used >= max`. Three hard rules:
 *
 *  1. **THE ALLOWANCE MAY NEVER BE ZERO.** A zero anywhere in this table blocks
 *     a brand-new account at ZERO held, with nothing to complete or archive to
 *     escape — which is precisely the production lockout removed on 2026-08-03,
 *     where `calculateCapacity` returned 0 below an unlock level. Pinned by
 *     `achievementBadges.test.ts` ("never hands out an allowance of zero") and
 *     mutation-verified. Nothing here may ever consult a LEVEL.
 *  2. **A user over the number is not in an error state, and loses nothing.**
 *     Ranks are derived from completions, so a fresh domain starts at
 *     `baseCapacity` while an established one may already hold more. Those
 *     accounts keep every record — `used >= max` is simply already true, so
 *     they are stopped from ADDING and otherwise untouched. Over is "this domain
 *     is full", which is information, not a fault.
 *  3. **A BLOCK MUST HAVE A WAY OUT.** `utils/capacityEscapes.ts` may only offer
 *     a remedy where it provably reduces the count that blocked, and the Inbox
 *     is offered for every type — including the ones with no other remedy.
 *
 * ⚠️ THE COUNT THAT EARNS AND THE COUNT THAT FILLS ARE DIFFERENT NUMBERS.
 * `capacityFor(type, count)` takes the LIFETIME COMPLETION count (what raises
 * the rank); what fills the allowance is the ACTIVE holding. Passing the wrong
 * one silently produces a plausible bar that measures nothing.
 *
 * ⚠️ IT DISAGREES WITH THE WORKBOOK, ON PURPOSE AND ONLY FOR NOW.
 * `PROGRESSIVE_UNLOCK_PLAN.xlsx` → «Capacity Ladders» models the ENFORCED cap as
 * **GLOBAL and ACTIVE-only** ("5×7 domains = 35 would recreate the overwhelm").
 * This table is per DOMAIN and answers a different question — *how full is this
 * domain?* — which is the one an action-type page can actually ask. It is also
 * the only source that covers **Events and Skills**, which the workbook has no
 * row for, and it needs no System Mastery stage (nothing derives one). When the
 * epic's engine lands, the ENFORCED number comes from the workbook and these
 * stay descriptive — or the two are reconciled deliberately, in one place.
 */

/**
 * The seven rungs every action type climbs, lowest first.
 *
 * ⭐ RARITY NAMES, not craft names (owner, 2026-08-02). A badge tier says how
 * rare the achievement is; the CRAFT ladder is the domain title (Trainee →
 * Visionary), and using the same vocabulary for both made a badge look like a
 * second, contradictory rank for the same domain.
 *
 * ⚠️ The `id`s keep their original slugs on purpose. They are not display
 * strings and nothing stores them, but they ARE the stable handle if a rank is
 * ever persisted or referenced from config — renaming them buys nothing and
 * would make any future stored value ambiguous.
 */
export const BADGE_RANKS = [
  { id: 'initiate',     label: 'Common',       color: '#B0BEC5' },
  { id: 'apprentice',   label: 'Uncommon',     color: '#4CAF50' },
  { id: 'expert',       label: 'Rare',         color: '#2196F3' },
  { id: 'master',       label: 'Epic',         color: '#9C27B0' },
  { id: 'legend',       label: 'Legendary',    color: '#FF9800' },
  { id: 'mythic',       label: 'Mythic',       color: '#F44336' },
  { id: 'transcendent', label: 'Transcendent', color: '#FFD700' },
] as const;

export type BadgeRankId = (typeof BADGE_RANKS)[number]['id'];
export const RANK_COUNT = BADGE_RANKS.length;

export type ActionTypeId =
  | 'tasks' | 'habits' | 'projects' | 'events' | 'quests'
  | 'goals' | 'abilities' | 'skills' | 'programs' | 'routines';

export interface ActionType {
  id: ActionTypeId;
  label: string;
  /** Lucide icon name — resolved by the view. */
  icon: string;
  /** What the count MEANS, e.g. "completed" — used in the badge description. */
  verb: string;
  /**
   * The badge's own name — what you are CALLED for doing this a lot.
   *
   * ⭐ Distinct from `label`, which names the action. The card shows both: the
   * action tells you which ladder you are on, the badge name is the reward for
   * climbing it. ⚠️ Every one is checked against the domain and user TITLE
   * ladders — a badge called "Adept" or "Champion" would read as a second,
   * contradictory rank for the same person.
   */
  badgeName: string;
  /** Count needed for each rank, lowest first. Seven entries. */
  thresholds: readonly number[];
  /**
   * How many of this type a DOMAIN may hold at once, per rank. Seven entries.
   * `baseCapacity` is the allowance before any rank is earned.
   *
   * ⚠️ Paired with `thresholds` on purpose — rank `i` costs `thresholds[i]` and
   * grants `capacity[i]`. Splitting them into a second table would let the two
   * arrays drift out of alignment with nothing to catch it.
   */
  baseCapacity: number;
  capacity: readonly number[];
  /**
   * ⚠️ WHAT THE CAPACITY IS COUNTED AGAINST, when it is not a per-domain
   * holding. Goals are capped PER QUEST and Skills PER ABILITY, so their
   * numbers mean something different from the other eight — "10 goals" as a
   * domain total would be plain wrong. Every surface that prints a capacity
   * appends this, so a per-parent cap can never be read as a domain total.
   */
  capacityPer?: string;
}

/**
 * ⚠️⚠️ THE OWNER'S AUTHORED TABLE (2026-08-02) — a deliberate, large rebalance,
 * not a tweak. Every requirement fell sharply against what shipped before
 * (Transcendent Events 750 → 50, Goals 400 → 20, Projects 250 → 20). A badge
 * rank is DERIVED from a count at render time, so nothing is corrupted and no
 * data moves — but the day this lands, existing accounts jump several tiers at
 * once. That is intended.
 *
 * ⚠️ `baseCapacity` is the table's Tier 0 capacity; `thresholds[i]`/`capacity[i]`
 * are its Tier i+1 columns.
 *
 * ⚠️ Two corrections to the draft, confirmed by the owner: Events capacity at
 * Transcendent 30 → 60 (as drafted it was the table's only DECREASE, so the top
 * tier would have reduced what a domain can hold), and Projects capacity at Rare
 * 6 → 8 (it was flat, so that tier bought nothing). Routines had no row and
 * takes the same numbers as Projects.
 */
/**
 * ⚠️⚠️ **`thresholds` IS THE BADGE LADDER AND THE CAPACITY LADDER, ONE ARRAY DOING
 * TWO JOBS.** `capacityFor` calls `earnedRankCount(count, thresholds)` — the same
 * call that decides whether somebody holds Common. So RAISING A THRESHOLD TAKES A
 * BADGE OFF SOMEBODY WHO ALREADY EARNED IT, on a surface that shows real earned
 * dates. To re-pace growth, move `capacity`; leave `thresholds` alone.
 *
 * ⭐ Bases raised 2026-09-20 (owner) after a live run: quests 1→3, habits 3→6,
 * programs 2→3, with the `capacity` steps regularised so only the start is
 * kinder. A Growth Profile's first rank brings two quests into one domain, and a
 * base of 1 refused the second — one screen after the profile said to apply it.
 * Habits (six by rank 2) and programs (two at rank 1, against a base of 2) were
 * the next two walls, fixed in the same pass rather than discovered one at a time.
 * ⚠️ CORRECTION (2026-09-20, second pass): the commit that made this change says
 * "the ceiling is unchanged". That is true of habits (30) and programs (18), and
 * NOT of quests — regularising its steps to +1 from a base of 3 carried the top
 * one from 9 to 10.
 *
 * ⭐⭐ QUESTS' CEILING IS 12, AND THAT IS A DECISION (owner, 2026-09-21). Asked to
 * pin it back to 9, and it turned out 9 IS UNREACHABLE AT THIS BASE: the invariant
 * test below — `capacity[0] > baseCapacity`, then strictly increasing — needs seven
 * distinct values above 3, and 4…9 supplies only six. So 10 was never a choice; it
 * was the FLOOR the invariant forces at a base of 3. The three real options were
 * drop the base to 2 (which reopens the wall this whole change exists to remove —
 * a rank brings two quests into one domain), allow a flat top tier (which costs the
 * guard that caught Projects' "Rare bought nothing"), or raise the ceiling on
 * purpose. The owner raised it.
 * ⚠️ So do NOT "correct" 12 back toward 9 — the arithmetic will simply refuse, and
 * the test is the thing telling you so. To lower this ceiling you must first lower
 * `baseCapacity`, and that re-opens H5. The shape follows the table's own idiom:
 * +1 through the early tiers as the old quests row did, accelerating at the top as
 * Projects and Programs do.
 * ⚠️ These are per DOMAIN, and 1 was never realistic: the owner's own account ran
 * at 4 of 1 active Personal quests.
 */
export const ACTION_TYPES: readonly ActionType[] = [
  // ⚠️ ORDER IS THE GRID (owner, 2026-08-02). The badge grid is five columns
  // wide, so this array IS the two rows on screen: quests · abilities ·
  // projects · routines · programs, then goals · skills · tasks · habits ·
  // events. Nothing indexes this list (consumers use `.map` and
  // ACTION_TYPE_BY_ID), so reordering is purely visual — but reordering it
  // DOES move the cards.
  { id: 'quests', label: 'Quests', icon: 'Target', verb: 'completed', badgeName: 'Questbearer',
    thresholds: [3, 5, 7, 9, 12, 15, 18],
    baseCapacity: 3, capacity: [4, 5, 6, 7, 8, 10, 12] },
  { id: 'abilities', label: 'Abilities', icon: 'Network', verb: 'levels gained in', badgeName: 'Cultivator',
    thresholds: [3, 5, 7, 9, 12, 15, 18],
    baseCapacity: 2, capacity: [4, 6, 8, 10, 12, 14, 16] },
  { id: 'projects', label: 'Projects', icon: 'FolderKanban', verb: 'completed', badgeName: 'Builder',
    thresholds: [1, 3, 6, 9, 12, 15, 20],
    baseCapacity: 2, capacity: [4, 6, 8, 10, 12, 15, 20] },
  // ⚠️ No row in the owner's table; takes Projects' numbers on their instruction.
  { id: 'routines', label: 'Routines', icon: 'ListChecks', verb: 'runs of', badgeName: 'Keeper',
    thresholds: [1, 3, 6, 9, 12, 15, 20],
    baseCapacity: 2, capacity: [4, 6, 8, 10, 12, 15, 20] },
  { id: 'programs', label: 'Programs', icon: 'BookOpen', verb: 'finished', badgeName: 'Graduate',
    thresholds: [1, 3, 6, 9, 12, 15, 20],
    baseCapacity: 3, capacity: [5, 7, 9, 11, 13, 15, 18] },
  { id: 'goals', label: 'Goals', icon: 'Flag', verb: 'completed', badgeName: 'Achiever',
    thresholds: [5, 7, 9, 12, 15, 18, 20],
    baseCapacity: 3, capacity: [4, 5, 6, 7, 8, 9, 10], capacityPer: 'quest' },
  { id: 'skills', label: 'Skills', icon: 'GraduationCap', verb: 'mastered', badgeName: 'Artificer',
    thresholds: [5, 7, 10, 15, 20, 25, 30],
    baseCapacity: 5, capacity: [7, 10, 15, 20, 25, 30, 36], capacityPer: 'ability' },
  { id: 'tasks', label: 'Tasks', icon: 'CheckSquare', verb: 'completed', badgeName: 'Finisher',
    thresholds: [15, 30, 45, 65, 80, 100, 150],
    baseCapacity: 5, capacity: [7, 9, 12, 15, 18, 25, 30] },
  { id: 'habits', label: 'Habits', icon: 'Repeat', verb: 'check-ins on', badgeName: 'Ritualist',
    thresholds: [10, 15, 30, 45, 60, 75, 100],
    baseCapacity: 6, capacity: [8, 10, 12, 15, 18, 22, 30] },
  { id: 'events', label: 'Events', icon: 'CalendarDays', verb: 'completed', badgeName: 'Chronicler',
    thresholds: [7, 10, 15, 21, 30, 40, 50],
    baseCapacity: 5, capacity: [10, 15, 21, 25, 30, 45, 60] },
] as const;

export const ACTION_TYPE_BY_ID: Record<ActionTypeId, ActionType> =
  Object.fromEntries(ACTION_TYPES.map(a => [a.id, a])) as Record<ActionTypeId, ActionType>;

/**
 * How many ranks a count has earned: 0 = none yet, 7 = the top.
 *
 * ⚠️ Returns a COUNT, not an index, so "none earned" is 0 rather than -1 and
 * callers never have to special-case a negative. `earned - 1` indexes the
 * current rank; `earned` indexes the next one.
 */
export function earnedRankCount(count: number, thresholds: readonly number[]): number {
  let earned = 0;
  for (const t of thresholds) {
    if (count >= t) earned++;
    else break;   // thresholds ascend — the first miss ends it
  }
  return earned;
}

/**
 * The capacity a domain has unlocked, including the base when nothing is earned.
 *
 * ⚠️ `count` is the LIFETIME COMPLETION count for this type in this domain — the
 * number that raises the rank. It is NOT the active holding that fills the
 * allowance. Handing it the wrong one returns a real-looking number that answers
 * a question nobody asked.
 */
export function capacityFor(type: ActionType, count: number): number {
  const earned = earnedRankCount(count, type.thresholds);
  return earned === 0 ? type.baseCapacity : type.capacity[earned - 1];
}

/** What the NEXT rank would raise capacity to — null at the top of the ladder. */
export function nextCapacityFor(type: ActionType, count: number): number | null {
  const earned = earnedRankCount(count, type.thresholds);
  return earned >= RANK_COUNT ? null : type.capacity[earned];
}

/** The count still needed for the next rank — null once the ladder is finished. */
export function toNextRank(count: number, thresholds: readonly number[]): {
  needed: number; target: number; progress: number;
  /** The threshold this tier STARTS from — 0 for the first. */
  floor: number;
  /** Progress WITHIN this tier: how many of `span` are done. */
  inTier: number;
  /** How many this tier asks for in total (`target − floor`). */
  span: number;
} | null {
  const earned = earnedRankCount(count, thresholds);
  if (earned >= thresholds.length) return null;
  const target = thresholds[earned];
  const floor = earned === 0 ? 0 : thresholds[earned - 1];
  const span = Math.max(1, target - floor);
  return {
    // ⚠️ No clamp needed and none given: `earned` stops at the FIRST threshold
    // not met, so `count` is always below `target` here. A `Math.max(0, …)`
    // would imply a negative is reachable and quietly hide it if one ever were.
    needed: target - count,
    target,
    progress: Math.min(100, Math.round(((count - floor) / span) * 100)),
    /**
     * ⭐⭐ EACH TIER STARTS AT ZERO. The bar was always measured from `floor`,
     * but the FRACTION beside it printed the raw lifetime count against the
     * absolute target — "7/15" for a tier that actually asks for 10 more — so
     * the number said the previous tier's work carried over while the bar said
     * it did not. These two make the tier-relative pair available so the label
     * can agree with the bar.
     */
    floor,
    inTier: count - floor,
    span,
  };
}

/**
 * The USER's rank for an action — the rank every domain has reached.
 *
 * ⭐⭐ THE MINIMUM ACROSS DOMAINS, deliberately. A user badge says "I am this
 * far along EVERYWHERE", so it can only move when the domain that is furthest
 * behind moves. Taking the max (or an average) would let one strong domain
 * mint a badge that claims something about a life you have not lived — and
 * would make the badge easier to earn the more domains you neglect.
 *
 * ⚠️ No domains at all → 0. An empty minimum is vacuously "all of them", which
 * would hand out the top rank for having nothing.
 */
export function userRankCount(perDomainRanks: readonly number[]): number {
  if (perDomainRanks.length === 0) return 0;
  return perDomainRanks.reduce((min, r) => Math.min(min, r), Infinity);
}
